import React, { useState, useCallback, useMemo, useRef } from 'react';

import config from 'config';
import { transparentize } from 'polished';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { findKeyInNestedStructure } from '~/account/components/InitiateReturnLayout/helper';
import CloseIcon from '~/icons/components/CloseIcon/CloseIcon';
import PlayIcon from '~/icons/components/PlayIcon/PlayIcon';
import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import BaseScrollOffset from '~/shared/components/ScrollOffset';
import WishlistToggleButton from '~/shared/components/WishlistToggleButton';
import { isReactVideoEnded } from '~/shared/utils/helpers';
import scrollToRef from '~/shared/utils/scrollToRef';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import {
  trackVideoPlaybackStarted,
  trackVideoPlaybackCompleted,
} from '~/shoppableVideo/actions';
import {
  Button,
  Carousel,
  ImageBrowser,
  Picture,
  PictureZoom as BasePictureZoom,
  MouseActivation,
  ImageBrowserHeroImage as BaseHeroImage,
  ProductVideoModal,
  SecondaryImages as BaseSecondaryImages,
  useHasTouch,
  useProductDetailContext,
  useProductImages,
  useTheme,
  useModelImageContext,
  mobile,
  SkeletonLine,
  useBreakpoint,
  desktop,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { trackProductDetailImageViewed } from '~/techstyle-shared/react-products';
import { useSession, useLocalStorage } from '~/techstyle-shared/redux-core';

import EnhancedMemberModel from '../EnhancedMemberModel';
import { useMemberModelContext } from '../EnhancedMemberModelProvider';
import ModelIndicator from '../ModelIndicator';

const messages = defineMessages({
  playVideo: {
    id: 'global_cta.play_video',
    defaultMessage: 'Play Video',
  },
});

const imageAspectRatio = config.get('public.brand.pdpImageAndVideoAspectRatio');

const ScrollOffset = styled(BaseScrollOffset)`
  ${({ $isQuickView }) =>
    !$isQuickView &&
    css`
      height: unset;

      @media (min-width: ${({ theme }) =>
          theme.breakpoints.tablet.minWidth}px) {
        position: sticky;
        top: 16px;
      }
    `}

  ${({ $isQuickView }) =>
    $isQuickView &&
    css`
      ${desktop`
        height: 100%;
      `};

      ${mobile`
      @media (min-width: ${({ theme }) =>
        theme.breakpoints.tablet.minWidth}px) {
        position: sticky;
        top: 16px;
      }
  `};
    `}
`;

const ImageWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 480px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    width: 320px;
  }

  ${mobile`
    width: 100%;
  `};

  ${({ $isQuickView }) =>
    $isQuickView &&
    css`
      ${desktop`
        height: 100%;
        width: 100%;
        gap: 8px;
      `};

      @media (max-width: ${({ theme }) =>
          theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
          theme.breakpoints.mobile.maxWidth + 1}px) {
        width: 100%;
        height: auto;
        gap: 8px;
      }
    `}
`;

const SecondaryImages = styled(BaseSecondaryImages)`
  flex: 0 0 auto;

  @media (max-width: ${({ theme }) =>
      theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
      theme.breakpoints.mobile.maxWidth + 1}px) {
    ${({ $isQuickView }) =>
      $isQuickView
        ? css`
            display: flex;
            width: 103px;
            margin-right: 15px;
          `
        : css`
            display: none;
          `}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile.maxWidth}px) {
    display: none;
  }

  ${({ $isQuickView }) =>
    !$isQuickView &&
    css`
      width: 103px;
      margin-right: 15px;
    `}
`;

const ImageBrowserHeroImage = styled(BaseHeroImage)`
  width: 100%;
  @media (max-width: ${({ theme }) =>
      theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
      theme.breakpoints.mobile.maxWidth + 1}px) {
    ${({ $isQuickView }) =>
      $isQuickView
        ? css`
            display: block;
          `
        : css`
            display: none;
          `}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile.maxWidth}px) {
    display: none;
  }

  ${({ $isQuickView }) =>
    $isQuickView &&
    css`
      ${desktop`
        height: 100%;
      `};

      @media (max-width: ${({ theme }) =>
          theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
          theme.breakpoints.mobile.maxWidth + 1}px) {
        height: auto;
      }
    `}
`;

const toggleButtonStyle = css`
  position: absolute;
  width: 48px;
  height: 48px;
  top: 16px;
  right: 16px;
  z-index: 1;
  padding: 12px 10px;
`;

const heartIconStyle = css`
  width: 28px;
  height: 24px;
  color: ${({ theme }) => theme.colors.heartColor};
`;

const HeroImageWrapper = styled.div`
  position: relative;

  ${({ $isQuickView }) =>
    !$isQuickView &&
    css`
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
    `}

  ${({ $isQuickView }) =>
    $isQuickView &&
    css`
      @media (max-width: ${({ theme }) =>
          theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
          theme.breakpoints.mobile.maxWidth + 1}px) {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
      }

      ${mobile`
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
      `};
    `}
`;

const PictureZoom = styled(BasePictureZoom)`
  z-index: 0;

  ${({ isQuickView }) =>
    isQuickView &&
    css`
      ${mobile`
        height: 100%;
      `};
    `}
`;

const ZoomOutButton = styled(PictureZoom.ZoomOutButton)`
  height: 40px;
  width: auto;
  margin-bottom: 24px;
  padding: 0 24px;
  border-radius: 24px;
  border: none;
  font-weight: bold;
  line-height: 1.29;
  color: ${(props) => props.theme.colors.palette.white};
  background: ${(props) => props.theme.colors.sublabelBlack};
`;

const ZoomOverlayMessage = styled(PictureZoom.OverlayMessage)`
  opacity: 1;
  height: 40px;
  width: auto;
  padding: 0 24px;
  border-radius: 24px;
  border: none;
  font-weight: 700;
  color: ${(props) => props.theme.colors.palette.white};
  background: ${(props) => props.theme.colors.sublabelBlack};
  display: flex;
  align-items: center;
`;

const zoomOverlayStyle = css`
  background: rgba(238, 238, 238, 0.6);
`;

const ProgressBarCarousel = styled(Carousel)`
  display: none;
  @media (max-width: ${({ theme }) =>
      theme.breakpoints.tablet.maxWidth}px) and (min-width: ${({ theme }) =>
      theme.breakpoints.mobile.maxWidth + 1}px) {
    ${({ $isQuickView }) =>
      $isQuickView
        ? css`
            display: none;
          `
        : css`
            display: block;
            width: 100%;
          `}
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile.maxWidth}px) {
    display: block;
    width: 100%;
  }
`;

const progressIndicatorStyle = css`
  background: ${({ theme }) => theme.colors.backgroundDark};
`;

const progressBarStyle = css`
  background-color: ${({ theme }) =>
    transparentize(0.84, theme.colors.productDetailProgressBar)};
`;

const hiddenProgressBarStyle = css`
  visibility: hidden;
`;

const dialogStyle = css`
  width: 320px;
  border-radius: 4px;
  background-color: ${({ theme }) =>
    transparentize(0.5, theme.colors.palette.black)};
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

const VideoPosterImage = styled(Picture)`
  opacity: 0.7;
`;

const VideoImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  z-index: 1;
  position: absolute;
  width: 88px;
  height: 64px;
  padding: 16px 12px 12px;
  border-radius: 53px;
  background-color: ${({ theme }) =>
    transparentize(0.2, theme.colors.palette.black)};
`;

const PlayIconWrapper = styled.div`
  position: absolute;
  z-index: 1;
  width: 58px;
  padding: 10px 8px 6px;
  border-radius: 16px;
  background-color: ${({ theme }) =>
    transparentize(0.2, theme.colors.palette.black)};
`;

const closeButtonStyle = css`
  top: 16px;
  right: 16px;
  border-radius: 28px;
  width: 40px;
  height: 40px;
  box-shadow: 0 1px 2px 0
    ${({ theme }) => transparentize(0.96, theme.colors.palette.black)};
  border: solid 1px ${({ theme }) => theme.colors.closeButtonBorder};
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 11px;
  opacity: 0.24;
  &:hover,
  &:focus {
    opacity: 1;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    width: 48px;
    height: 48px;
    opacity: 1;
  }
  ${mobile`
    padding: 11px;
  `};
`;

const videoWrapperStyle = css`
  width: 320px;
  height: 480px;
  margin: 0 auto;
  ${mobile`
    height: 100%;
    width: 100%;
  `};
`;

const videoProps = {
  // setting playsinline will enables/disables in line media playback in the iOS web browser
  playsinline: true,
  wrapperStyle: videoWrapperStyle,
  aspectRatio: imageAspectRatio,
};

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 20px;
  color: ${({ theme }) => theme.colors.devyGray};
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    font-size: 24px;
    color: ${({ theme }) => theme.colors.closeIcon};
  }
`;

const CellsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 15px;
  width: 103px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    display: none;
  }
`;

const LoaderWrapper = styled.div`
  & + & {
    margin-top: 8px;
  }
`;

const SecondaryImageSkeleton = styled(SkeletonLine)`
  flex: 0 0 auto;
  height: 154px;
  width: 103px;
`;

const ImageHeroSkeleton = styled(SkeletonLine)`
  width: 100%;
  height: 543px;
`;

const cellStyle = css`
  ${mobile`
    width: 80%;
    margin-right: 6px;
  `};
`;

const MemberModelWrapper = styled.div`
  ${({ theme: { pdpStyles } }) => pdpStyles.enhancedMemberModel.button.wrapper};
`;

export const ProductDetailImagesLoading = () => {
  return (
    <ImageWrapper>
      <CellsWrapper>
        <LoaderWrapper>
          <SecondaryImageSkeleton />
        </LoaderWrapper>
        <LoaderWrapper>
          <SecondaryImageSkeleton />
        </LoaderWrapper>
        <LoaderWrapper>
          <SecondaryImageSkeleton />
        </LoaderWrapper>
      </CellsWrapper>
      <HeroImageWrapper>
        <ImageHeroSkeleton />
      </HeroImageWrapper>
    </ImageWrapper>
  );
};

export default function ProductDetailImages({ product: productFromProps }) {
  const quickViewContext = useQuickViewContext();
  const isQuickView = quickViewContext?.isQuickView;

  const [productVideoModalUrl, setProductVideoModalUrl] = useState(null);
  const dispatch = useDispatch();
  const carouselRef = useRef();
  const imageRef = useRef();
  const theme = useTheme();
  const { isLoggedIn } = useSession();
  const productDetailFromCtx = useProductDetailContext();
  const product = productFromProps || productDetailFromCtx.product;
  const {
    bundleProducts,
    isBundle,
    isPlusSizeSelected,
    isProductAvailableForUser,
  } = product;
  const hasTouch = useHasTouch();
  const intl = useIntl();
  const { isMobile, isDesktop } = useBreakpoint();
  const [hasOverlayBeenHidden, setHasOverlayBeenHidden] = useLocalStorage(
    'hasImageZoomOverlayBeenHidden',
    {
      defaultValue: false,
      enableSessionStorage: true,
    }
  );
  const secondarySite = useSecondarySite();
  const isMysteryBox = product?.isMysteryBundle;

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback((url) => {
    setProductVideoModalUrl(url);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleOnPlay = () => {
    dispatch(trackVideoPlaybackStarted());
  };

  const {
    activeModelImages,
    activeModel,
    modelSets: memberModels,
  } = useModelImageContext();
  const [{ model }] = memberModels ?? [{ model: null }];
  const imageSets = useProductImages({
    images: activeModelImages,
  });

  const { hasModelInfo } = useMemberModelContext();
  const shouldDisplayModelIndicator = model && hasModelInfo;

  const secondaryImageBreakpoints = [{ minWidth: 0, pictureWidth: 103 }];
  const heroImageBreakpoints = [
    { ...theme.breakpoints.mobile, pictureWidth: 767 },
    { ...theme.breakpoints.tablet, pictureWidth: 320 },
    { minWidth: 1024, pictureWidth: 362 },
  ];

  const hasSamVideo = findKeyInNestedStructure(imageSets, 'isVideo');
  const consoleVideoUrl = product.video && product.video.url;
  const showVideoFromConsole = consoleVideoUrl && !hasSamVideo;

  const handleProgress = ({ playedSeconds, loadedSeconds }) => {
    const videoHasEnded = isReactVideoEnded(playedSeconds, loadedSeconds);
    if (videoHasEnded) {
      dispatch(trackVideoPlaybackCompleted());
    }
  };

  const imageSetsWithVideoImage = useMemo(() => {
    if (imageSets.length && consoleVideoUrl) {
      return [
        ...imageSets,
        imageSets[0].map((source) => ({ ...source, isVideoPoster: true })),
      ];
    }

    return imageSets;
  }, [imageSets, consoleVideoUrl]);

  if (!imageSetsWithVideoImage.length) {
    return null;
  }

  const isMobileCarousel = isMobile && imageSetsWithVideoImage.length > 1;
  const mobileCarouselProps = isMobileCarousel
    ? { cellStyle, cellAlign: 'left' }
    : {};

  const renderVideoMobile = () => {
    return (
      <VideoImageWrapper data-testid="pdp-video">
        <PlayButton
          type="button"
          aria-label={intl.formatMessage(messages.playVideo)}
          onClick={() => openModal(product.video?.url)}
        >
          <PlayIcon size={27} color={theme.colors.palette.white} />
        </PlayButton>
        <VideoPosterImage
          aspectRatio={imageAspectRatio}
          breakpoints={heroImageBreakpoints}
          alt={product.label}
          sources={imageSetsWithVideoImage[0]}
        />
      </VideoImageWrapper>
    );
  };

  const renderVideoDesktop = () => {
    return (
      <VideoImageWrapper data-testid="pdp-video">
        <PlayIconWrapper>
          <PlayIcon size={22} color={theme.colors.palette.white} />
        </PlayIconWrapper>
        <VideoPosterImage
          aspectRatio={imageAspectRatio}
          breakpoints={secondaryImageBreakpoints}
          alt={product.label}
          sources={imageSetsWithVideoImage[0]}
        />
      </VideoImageWrapper>
    );
  };

  /* eslint-disable react/prop-types */
  const renderPicture = (props) => {
    if (props.sources[0].isVideoPoster) {
      return renderVideoDesktop();
    }
    return <Picture {...props} />;
  };
  /* eslint-enable react/prop-types */
  const dispatchTrackImage = (index) => {
    dispatch(
      trackProductDetailImageViewed({
        model: activeModel,
        bundleProducts,
        image: activeModelImages[index] || {},
        index,
        isBundle,
        isPlusSizeSelected,
        isProductAvailableForUser,
        product,
      })
    );
  };

  return (
    <ImageBrowser images={imageSetsWithVideoImage}>
      {({
        isZoomed,
        setIsZoomed,
        images,
        activeImageIndex,
        setActiveImageIndex,
      }) => {
        const zoomIn = () => {
          if (carouselRef.current) {
            carouselRef.current.options.draggable = false;
            carouselRef.current.updateDraggable();
          }
          setIsZoomed(true);
          scrollToRef(imageRef, { block: 'start' });
        };

        const zoomOut = () => {
          if (carouselRef.current) {
            carouselRef.current.options.draggable = true;
            carouselRef.current.updateDraggable();
          }
          setIsZoomed(false);
        };

        const handleClick = (index) => {
          if (images[index][0].isVideoPoster) {
            openModal();
          } else {
            setActiveImageIndex(index);
            dispatchTrackImage(index);
          }
        };

        const showMemberModelButton =
          !isQuickView && model && hasModelInfo && isMobile;

        return (
          <ScrollOffset
            ref={imageRef}
            $isQuickView={isQuickView}
            offsetOverride={isQuickView && isDesktop}
          >
            <ImageWrapper $isQuickView={isQuickView}>
              <SecondaryImages
                breakpoints={secondaryImageBreakpoints}
                renderPicture={renderPicture}
                onClick={handleClick}
                data-autotag="pdp-secondary-media-content"
                $isQuickView={isQuickView}
              />
              <HeroImageWrapper
                data-autotag="pdp-media-content"
                $isQuickView={isQuickView}
              >
                {isLoggedIn && !secondarySite && !isMysteryBox ? (
                  <WishlistToggleButton
                    autoTagPrefix="pdp"
                    toggleButtonStyle={toggleButtonStyle}
                    heartIconStyle={heartIconStyle}
                  />
                ) : null}
                {showMemberModelButton && (
                  <MemberModelWrapper>
                    <EnhancedMemberModel product={product} />
                  </MemberModelWrapper>
                )}
                <PictureZoom
                  overlayContent={<ZoomOverlayMessage />}
                  overlayStyle={zoomOverlayStyle}
                  overlayEnabled={hasTouch && !hasOverlayBeenHidden}
                  onOverlayHide={() => setHasOverlayBeenHidden(true)}
                  zoomOutTrigger={hasTouch && <ZoomOutButton />}
                  pictureKey={activeImageIndex}
                  data-autotag="pdp-img-zoom-in"
                  isQuickView={isQuickView}
                  shouldDisplayModelIndicator={shouldDisplayModelIndicator}
                >
                  <ImageBrowserHeroImage
                    aspectRatio={imageAspectRatio}
                    breakpoints={heroImageBreakpoints}
                    alt={product.label}
                    $isQuickView={isQuickView}
                  />
                </PictureZoom>
                <ProgressBarCarousel
                  $isQuickView={isQuickView}
                  draggable
                  freeScroll={false}
                  pageDots={false}
                  prevNextButtons={false}
                  wrapAround={false}
                  contain
                  enableProgressBar
                  progressBarHeight={4}
                  progressBarStyle={
                    isZoomed ? hiddenProgressBarStyle : progressBarStyle
                  }
                  progressIndicatorStyle={progressIndicatorStyle}
                  flickityRef={carouselRef}
                  progressBarAutoTag="pdp-media-carousel-progressbar"
                  events={{
                    change: dispatchTrackImage,
                  }}
                  {...mobileCarouselProps}
                >
                  {imageSets.map((image, index) => {
                    const isVideo = image?.[0]?.isVideo;
                    const videoPath = image?.[0] && [image[0]];
                    const url = videoPath[0].videoPath;

                    if (isVideo && videoPath) {
                      return (
                        <>
                          <VideoImageWrapper key={index}>
                            <PlayButton
                              type="button"
                              aria-label={intl.formatMessage(
                                messages.playVideo
                              )}
                              onClick={() => openModal(url)}
                            >
                              <PlayIcon
                                size={27}
                                color={theme.colors.palette.white}
                              />
                            </PlayButton>
                            <VideoPosterImage
                              aspectRatio={imageAspectRatio}
                              breakpoints={heroImageBreakpoints}
                              alt={product.label}
                              sources={videoPath}
                            />
                          </VideoImageWrapper>
                        </>
                      );
                    }

                    return (
                      <PictureZoom
                        overlayContent={
                          <ZoomOverlayMessage data-autotag="pdp-img-zoom-in" />
                        }
                        overlayStyle={zoomOverlayStyle}
                        overlayEnabled={!hasOverlayBeenHidden}
                        onOverlayHide={() => setHasOverlayBeenHidden(true)}
                        zoomOutTrigger={
                          hasTouch && (
                            <ZoomOutButton data-autotag="pdp-img-zoom-out" />
                          )
                        }
                        key={index}
                        pictureKey={index}
                        onZoomIn={zoomIn}
                        onZoomOut={zoomOut}
                        mouseActivation={MouseActivation.DOUBLE_CLICK}
                        data-autotag="pdp-img-zoom-in"
                      >
                        <Picture
                          sources={image}
                          alt={product.label}
                          aspectRatio={imageAspectRatio}
                          breakpoints={heroImageBreakpoints}
                        />
                      </PictureZoom>
                    );
                  })}
                  {showVideoFromConsole ? renderVideoMobile() : null}
                </ProgressBarCarousel>

                <ProductVideoModal
                  title={intl.formatMessage(messages.playVideo)}
                  isOpen={modalOpen}
                  onExit={closeModal}
                  dialogStyle={dialogStyle}
                  closeIcon={<StyledCloseIcon size={16} />}
                  videoProps={{
                    ...videoProps,
                    onProgress: handleProgress,
                    onPlay: handleOnPlay,
                  }}
                  closeButtonStyle={closeButtonStyle}
                  product={{ video: { url: productVideoModalUrl } }}
                />

                {shouldDisplayModelIndicator && <ModelIndicator />}
              </HeroImageWrapper>
            </ImageWrapper>
          </ScrollOffset>
        );
      }}
    </ImageBrowser>
  );
}

ProductDetailImages.propTypes = {
  product: PropTypes.object,
};
